import React, { useCallback, useRef, useState } from 'react';
import useModalBehavior from './useModalBehavior';
import { GOLD, GOLD_LT, CREAM, INK } from '../theme';

// Generic ready-to-wear measurements in inches — not garment-specific (no
// per-dress measurement data exists anywhere in this app) and not
// admin-editable. Intended as a rough reference alongside Custom Size, not
// a precise fit guarantee.
const SIZE_ROWS = [
  { size: 'XS', bust: '32–33', waist: '25–26', hip: '35–36' },
  { size: 'S', bust: '34–35', waist: '27–28', hip: '37–38' },
  { size: 'M', bust: '36–37', waist: '29–30', hip: '39–40' },
  { size: 'L', bust: '38–40', waist: '31–33', hip: '41–43' },
  { size: 'XL', bust: '41–43', waist: '34–36', hip: '44–46' },
];

const thStyle = { textAlign: 'left', fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, padding: '8px 10px', borderBottom: '1px solid rgba(201,162,75,.3)' };
const tdStyle = { padding: '10px', borderBottom: '1px solid rgba(201,162,75,.15)', color: CREAM };

export default function SizeChart() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  // Same contract as the other dialogs: Escape closes, Tab stays inside, the
  // page behind stops scrolling, focus returns to the trigger.
  useModalBehavior(open, close, panelRef);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, background: 'none', border: 'none', borderBottom: `1px solid ${GOLD_LT}`, padding: 0, cursor: 'pointer' }}
      >
        Size Chart
      </button>

      {open && (
        <div
          role="presentation"
          onClick={close}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,7,4,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
        >
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ij-sizechart-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{ outline: 'none', background: INK, border: `1px solid ${GOLD}`, padding: 'clamp(24px,4vw,36px)', maxWidth: 460, width: '100%', fontFamily: "'Cormorant Garamond', serif" }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 id="ij-sizechart-title" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 24, margin: 0, color: CREAM }}>Size Chart</h3>
              {/* Matched to the What's New dialog — the two used to close with
                  visibly different buttons in different boxes. */}
              <button onClick={close} aria-label="Close size chart" style={{ background: 'rgba(10,7,4,.55)', border: '1px solid rgba(201,162,75,.5)', color: CREAM, fontSize: 15, lineHeight: 1, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>✕</button>
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(239,228,210,.6)', margin: '0 0 18px' }}>
              General measurements in inches. For an exact fit, mention your measurements when you book or message us — Custom Size is always available.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 320, borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Size</th>
                    <th style={thStyle}>Bust</th>
                    <th style={thStyle}>Waist</th>
                    <th style={thStyle}>Hip</th>
                  </tr>
                </thead>
                <tbody>
                  {SIZE_ROWS.map((row) => (
                    <tr key={row.size}>
                      <td style={tdStyle}>{row.size}</td>
                      <td style={tdStyle}>{row.bust}"</td>
                      <td style={tdStyle}>{row.waist}"</td>
                      <td style={tdStyle}>{row.hip}"</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
